import React from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Link as MuiLink,
} from '@mui/material';
import { Lock } from 'lucide-react';
import api from '../axios/api';
import { showNotification } from '../redux/slices/notificationSlice';
import { useDispatch } from 'react-redux';

// Forget Password Schema
const ForgetPasswordSchema = yup.object().shape({
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
});

const ForgetPassword = () => {
  const dispatch = useDispatch();
  const form = useForm({
    resolver: yupResolver(ForgetPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const { register, handleSubmit, formState: { errors }, setValue } = form;

  const onSubmit = async (data) => {
    try {
      
      const response = await api.get(`auth/forgotpassword/${data.email}`);
      if (response.data.status === '200') {
        dispatch(
          showNotification({
            message: 'Link to reset your password has been sent to your email',
            type: "success",
          })
        );
      } else {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "error",
          })
        );
      }
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : typeof error === 'string'
          ? error
          : 'An unknown error occurred';
          dispatch(
            showNotification({
              message: errorMessage,
              type: "error",
            })
          );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        padding: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          width: { xs: '90%', md: '80%', lg: '70%' },
          backgroundColor: 'white',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: 3,
        }}
      >
        {/* Image Section */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'white',
            padding: 2,
          }}
        >
          <img
            src="/vectors/forgetPassword.jpg"
            alt="forget password"
            style={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Form Section */}
        <Card
          sx={{
            flex: 1,
            padding: 3,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <CardContent>
            <Box display="flex" alignItems="center" gap={1}>
              <Lock className='mb-3' />
              <Typography variant="h4" component="h4" gutterBottom>
                Forgot Password?
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Enter your Email to reset Your Password
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Email"
                  placeholder="john.doe@example.com"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  {...register('email')}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{ mt: 2 }}
                >
                  Send Email
                </Button>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="body2">
                    <Box display="inline-flex" gap={2}>
                      <a href="/login">
                        <MuiLink>Login</MuiLink>
                      </a>
                      <a href="/signup">
                        <MuiLink>Signup</MuiLink>
                      </a>
                    </Box>
                  </Typography>

                </Box>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ForgetPassword;
